import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AppNotification, DownloadAppQuery, JoiApp, JoiAppInfo, SurveyQueryData, SurveySubmission, SurveySubmissionData, privateAppInfoProps, standardizeAppInfo } from 'globals/store/app.data';
import { OnReset, Transitions } from './transition.service.helpers';
import { lastValueFrom, Subject } from 'rxjs';
import { JoiEvent } from 'globals/store/events.data';
import { SubscriptionHelper } from './helpers/subscriptions.helper';
import { UserService } from './user.service';
import { PublishStatus, getDefaultProgramTabNames, makeDefaultPublishInfoExcludedFields } from 'globals/store/share.data';
import { BrandingData, BrandingSelectData, createHomePageData } from 'globals/store/publish.data';
import { apiLink } from '../helpers/app-constants';
import { take } from 'rxjs/operators';
import { transformObservableToSinglePromise } from '../helpers/angular-helpers';
// import { brandDataReselector } from 'src/app/store/reselectors/publish.reselector';
import { collection, deleteDoc, doc, DocumentChange, DocumentData, Firestore, query, setDoc, updateDoc, where } from '@angular/fire/firestore';
import { appsActions } from '../store/app.era';
import { createSimpleId, jsonCopyObject, sortByPropOfNumber } from 'globals/helpers/helpers';
import { brandDataReselector } from '../store/reselectors/publish.reselector';
import { publicEventDataReselector, PublishReselectorData } from '../store/reselectors/events.reselector';

// separating this out into its own service for better separation of concerns / clarity between services
@Injectable({ providedIn: 'root' })
export class AppService implements OnReset {
  readonly api = `${environment.functionsURL}api/app`;
  readonly publicApi = `${environment.functionsURL}api/v1`;
  readonly _file = 'app.service.ts'
  public $destroy_selected_event: Subject<boolean> = new Subject();
  public eventId: string
  public accountId: string
  public attemptedAppDownloads: Set<string> = new Set()

  constructor(
    public http: HttpClient,
    public store: Store,
    private db: Firestore,
    private subscriptionHelper: SubscriptionHelper,
    private userService: UserService
  ) {}

  onReset(resetType, value = null) {
    switch (resetType) {
      case Transitions.OnEnterEvent:
        // console.log('subscribing to shareables...')
        const { eventId, accountId } = value as JoiEvent
        this.eventId = eventId
        this.accountId = accountId
        return this.getEventAppInfos()
      case Transitions.OnExitUser:
        this.$destroy_selected_event.next(true)
        this.eventId = undefined
        this.accountId = undefined
      case Transitions.OnExitEvent:
        this.$destroy_selected_event.next(true)
        this.eventId = undefined
        this.accountId = undefined
        break
      default:
        break;
    }
  }

  getEventAppInfos() {
    this.attemptedAppDownloads = new Set()
    const col = query<any, DocumentData>(collection(this.db, 'apps'), where('eventId', '==', this.eventId))
    this.subscriptionHelper.subscribeToCollectionsWithHandlerAndStopper<JoiAppInfo>(col, 'apps', this.handleAppsFromDB.bind(this), this.$destroy_selected_event, this._file, 'getEventApps')

    const notificationsCollection = query<any, DocumentData>(collection(this.db, 'appNotifications'), where('eventId', '==', this.eventId))
    this.subscriptionHelper.subscribeToCollectionsWithHandlerAndStopper<AppNotification>(notificationsCollection, 'appNotifications', this.handleNotificationsFromDB.bind(this), this.$destroy_selected_event, this._file, 'getEventApps - notifications')

    const notificationTest = query<any, DocumentData>(collection(this.db, 'appNotifications'), where('pushTime', '<=' , 1702746900000), where('pushTime', '>=', 1702746900000))
    this.subscriptionHelper.subscribeToCollectionsWithHandlerAndStopper<AppNotification>(notificationTest, 'appNotifications', this.handleTest.bind(this), this.$destroy_selected_event, this._file, 'getEventApps - notifications test')

  }

  handleTest(changes:DocumentChange<AppNotification>[]) {
    const toAddOrUpdate = changes.filter(change => change.type !== 'removed').map(change => change.doc.data())
    if (toAddOrUpdate.length) console.log('Test result', toAddOrUpdate[0])
  }

  handleAppsFromDB(changes:DocumentChange<JoiAppInfo>[]){
    const toDelete = changes.filter(change => change.type === 'removed').map((change:DocumentChange<JoiAppInfo>)=> change.doc.data())
    const toAddOrUpdate = changes.filter(change => change.type !== 'removed').map((change:DocumentChange<JoiAppInfo>)=> {
      const appInfo = change.doc.data()
      standardizeAppInfo(appInfo)
      return appInfo
    })
    toDelete.forEach(app => this.store.dispatch(appsActions.removeAppInfo(app.id)))
    if (toAddOrUpdate.length) {
      this.store.dispatch(appsActions.loadAppInfos(toAddOrUpdate))
    }
  }

  handleNotificationsFromDB(changes:DocumentChange<AppNotification>[]){
    const toDelete = changes.filter(change => change.type === 'removed').map(change => change.doc.data())
    const toAddOrUpdate = changes.filter(change => change.type !== 'removed').map(change => change.doc.data())
    if (toDelete.length) this.store.dispatch(appsActions.removeAppNotifications(toDelete))
    if (toAddOrUpdate.length) this.store.dispatch(appsActions.loadAppNotifications(toAddOrUpdate))
  }

  async submitSurvey(submitData: SurveySubmissionData): Promise<boolean> { // needs to match mobile app service
    try {
      const url = `${this.publicApi}/submit-survey`
      await this.http.post(url, submitData).pipe(take(1)).toPromise()
      return true
    } catch (response) {
      console.log(response)
      return false
    }
  }

  async createNotification(notification: AppNotification) {
    await setDoc(doc(this.db, '/appNotifications/' + notification.id), notification)
  }

  async updateNotification(notification: AppNotification) {
    await updateDoc(doc(this.db, '/appNotifications/' + notification.id), notification as { [x: string]: any; })
  }

  async deleteNotification(notificationId: string) {
    await deleteDoc(doc(this.db, '/appNotifications/' + notificationId))
  }

  async manuallyPushNotification(notificationId: string) {
    const result = await this.http.get(`${this.api}/send-app-notification/${notificationId}`).toPromise()
    console.log({ result })
  }

  async updateAppInfo(appInfo: JoiAppInfo) { 

    appInfo = jsonCopyObject(appInfo) as JoiAppInfo // I don't know why this is necessary, but it IS.  Something is changing the appInfo that is saved to the database before it is actually posted.  AppInfo here may just be a reference to a property on app-manager, so maybe that is why, but I really don't understand how it is happening.  If you remove this and activate an app without setting an end date it only saves in the browser not to firebase.
    const notificationsList:AppNotification [] = Object.values(appInfo.notifications)
    notificationsList.sort(sortByPropOfNumber('pushTime', -1))
    appInfo.notificationIds = notificationsList.map(n => n.id)

    const surveysList = Object.values(appInfo.surveys)
    surveysList.sort(sortByPropOfNumber('pushTime', -1))
    appInfo.surveyIds = surveysList.map(s => s.id)

    appInfo.tempNameAndEmail = !!appInfo.tempNameAndEmail
    appInfo.tempPasswordProtected = !!appInfo.tempPasswordProtected
    appInfo.nameAndEmail = !!appInfo.tempNameAndEmail
    appInfo.passwordProtected = !!appInfo.tempPasswordProtected

    appInfo.tempPassword = appInfo.tempPassword || ''
    appInfo.password = appInfo.tempPassword

    console.log(appInfo)

    await setDoc(doc(this.db, '/apps/' + appInfo.id), appInfo)
    const publicAppInfo = jsonCopyObject(appInfo)
    privateAppInfoProps.forEach(p => delete publicAppInfo[p])
    await setDoc(doc(this.db, '/publicApps/' + appInfo.id), publicAppInfo)
  }

  async updateApp(appInfoPassed: JoiAppInfo, status: PublishStatus, brandingData: BrandingData) { // for use with activate / update
    const appInfo = jsonCopyObject(appInfoPassed) as JoiAppInfo 
    appInfo.publisher = this.userService.fullName
    appInfo.publisherUid = this.userService.uid
    appInfo.publishedAt = new Date()
    appInfo.status = status
    appInfo.passwordProtected = !!appInfo.tempPasswordProtected
    if (!appInfo.passwordProtected) {
      delete appInfo.tempPassword
      delete appInfo.password
    }
    appInfo.nameAndEmail = !!appInfo.tempNameAndEmail
    appInfo.version += 0.01
    // PUBLISH HERE!!
    // app.appInfo = jsonCopyObject(appInfo)
    console.log("STILL NEED TO PUBLISH STATIC DATA!")
    const { eventData } = await transformObservableToSinglePromise<PublishReselectorData>(this.store.pipe(select(publicEventDataReselector, { appId: appInfo.id, filter: true })))
    eventData.excludedFieldIds = appInfo.excludedFieldIds
    eventData.tabNames = appInfo.tabNames

    console.log({ eventData })
    const app: JoiApp = {
      eventData,
      eventId: this.eventId,
      brandingData,
      id: appInfo.id
    }
    try {
      const url = `${this.api}/save-static-app`
      const saveAppResult = await this.http.post(url, { app }).pipe(take(1)).toPromise()
      console.log(saveAppResult)
      return await this.updateAppInfo(appInfo) 
    } catch (response) {
      console.log(response)
      return false
    }   
  }

  async updateAppFromTable(appInfo: JoiAppInfo) {
    const brandingSelectData: BrandingSelectData = {
      type: 'app',
      appId: appInfo.id
    }
    const brandingData = await lastValueFrom(this.store.select(brandDataReselector, brandingSelectData))
    return this.updateApp(appInfo, 'published', brandingData)
  }

  async deleteApp(appInfo: JoiAppInfo) {
    const url = `${this.api}/delete-static-app`
    const query: DownloadAppQuery = {
      id: appInfo.id,
      eventId: appInfo.eventId
    }
    const deleteAppPromises = [deleteDoc(doc(this.db, '/apps/' + appInfo.id)), deleteDoc(doc(this.db, '/publicApps/' + appInfo.id)), this.http.post(url, { query }).pipe(take(1)).toPromise() as any ]
    await Promise.all(deleteAppPromises)
  }

  async downloadSubmissions(query: SurveyQueryData) {
    const url = `${apiLink}/load-surveys`;
    console.log('Download submissions', { query })
    const result = await this.http.post(url, { query }).pipe(take(1)).toPromise() as { submissions: SurveySubmission[] }
    console.log(result)
    this.store.dispatch(appsActions.loadSurveyResponses(query.surveyId, result.submissions))
    return result.submissions
  }

  async downloadApp(appId: string) {
    console.log("Should be downloading app!", appId)
  }

  async createAppInfo(title: string, description: string): Promise<'success' | 'not-unique' | 'no-permission' | 'error' | boolean> { 
    const createdAt = new Date()
    const creator = this.userService.fullName
    const appInfo: Partial<JoiAppInfo> = { 
      title, description,  createdAt, creator,
      status: 'never published', 
      eventId: this.eventId, 
      excludedFieldIds: makeDefaultPublishInfoExcludedFields(), 
      venueId: null, 
      creatorUid: this.userService.uid, 
      homePageData: createHomePageData(), 
      version: 0.99, 
      tempPasswordProtected: false, 
      tempNameAndEmail: false,
      notifications: {},
      notificationIds: [],
      surveys: {},
      surveyIds: [],
      tabNames: getDefaultProgramTabNames(),
      testingDevices: [],
      fakeUsers: {},
      testingDevicePassword: createSimpleId(),
    } // becomes version 1 on first publish
    try {
      const url = `${this.api}/create-app-info`
      const id = await this.http.post(url, { appInfo }, { responseType: 'text' }).pipe(take(1)).toPromise()
      console.log("New app id: " + id)
      return 'success'
    } catch (response) {
      if(response && response.error === "Error: Not on program team of event") {
        return 'no-permission'
      } else if (response && response.error === "Error: App title must be unique among this event's apps") {
        return 'not-unique'
      }
      console.log(response)
      return 'error'
    }
    
  }
}